import { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApprovalDelegationSchema,
  ApprovalInboxItemSchema,
  DelegateCandidateSchema,
  type ApprovalDelegationDto,
  type ApprovalInboxItem,
} from '@taqon/contracts';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { z } from 'zod';
import { errorMessage, getData, postJson } from '../../shared/api/client';
import { recordPath, viewCapability } from '../../shared/records/routes';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { PersonName } from '../../shared/ui/person';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import {
  ApprovalStateChip,
  DecisionDialog,
  type Decision,
} from '../../shared/workflow/ApprovalPanel';
import { businessToday } from '../../shared/documents/dates';

const TABS = ['pending', 'decided', 'delegations'] as const;
const SubjectTypeNameSchema = z.object({ recordType: z.string(), displayName: z.string() });
type TabKey = (typeof TABS)[number];

/**
 * My approvals (FRD-CORE-018): the steps waiting for my decision across every module, the ones I
 * decided, and my delegations (FRD-CORE-012). Each item opens its record. The tab is kept in the
 * address, so it survives a refresh.
 */
export function ApprovalsPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: TabKey = TABS.includes(raw as TabKey) ? (raw as TabKey) : 'pending';
  return (
    <Stack spacing={3}>
      <PageHeader
        title="My approvals"
        description="Requests waiting for your decision, what you decided, and who approves for you while you are away."
      />
      <Tabs
        value={tab}
        onChange={(_e, value: TabKey) => {
          setParams(value === 'pending' ? {} : { tab: value });
        }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab value="pending" label="Waiting for me" />
        <Tab value="decided" label="Decided by me" />
        <Tab value="delegations" label="Delegations" />
      </Tabs>
      {tab === 'pending' && <Inbox kind="pending" />}
      {tab === 'decided' && <Inbox kind="decided" />}
      {tab === 'delegations' && <Delegations />}
    </Stack>
  );
}

function Inbox({ kind }: { kind: 'pending' | 'decided' }) {
  const [deciding, setDeciding] = useState<{ item: ApprovalInboxItem; decision: Decision } | null>(
    null,
  );
  const items = useQuery({
    queryKey: ['approvals', kind],
    queryFn: () => getData(`/api/v1/approvals/${kind}`, z.array(ApprovalInboxItemSchema)),
  });
  return (
    <Section>
      {items.isPending && <LoadingState label="Loading approvals" />}
      {items.isError && <ErrorState message={items.error.message} />}
      {items.data?.length === 0 && (
        <EmptyState
          title={
            kind === 'pending' ? 'Nothing is waiting for you' : 'You have not decided anything yet'
          }
        />
      )}
      {items.data !== undefined && items.data.length > 0 && (
        <Table size="small" aria-label={kind === 'pending' ? 'Waiting for me' : 'Decided by me'}>
          <TableHead>
            <TableRow>
              <TableCell>Request</TableCell>
              <TableCell>Step</TableCell>
              <TableCell>Submitted by</TableCell>
              <TableCell>{kind === 'pending' ? 'Waiting since' : 'Decided'}</TableCell>
              {kind === 'decided' && <TableCell>Decision</TableCell>}
              {kind === 'pending' && <TableCell align="right">Decide</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.data.map((item) => (
              <TableRow key={item.stepId} hover>
                <TableCell sx={{ minWidth: 200 }}>
                  <SubjectLink item={item} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="span"
                    sx={{ display: 'block' }}
                  >
                    {item.subjectRecordTypeName}
                    {item.delegateOf !== null && ' · as delegate'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.stepName}
                  {item.escalated && (
                    <Chip size="small" label="escalated" color="warning" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <PersonName name={item.initiatedByName} userId={item.initiatedBy} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {new Date(
                    kind === 'pending' ? item.since : (item.decidedAt ?? item.since),
                  ).toLocaleString()}
                </TableCell>
                {kind === 'decided' && (
                  <TableCell>
                    <ApprovalStateChip state={item.state} />
                  </TableCell>
                )}
                {kind === 'pending' && !item.canDecide && (
                  <TableCell align="right" sx={{ maxWidth: 320 }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.cannotDecideReason ?? 'You cannot decide this step.'}
                    </Typography>
                  </TableCell>
                )}
                {kind === 'pending' && item.canDecide && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => {
                        setDeciding({ item, decision: 'approve' });
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => {
                        setDeciding({ item, decision: 'return' });
                      }}
                    >
                      Return
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setDeciding({ item, decision: 'reject' });
                      }}
                    >
                      Reject
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {deciding !== null && (
        <DecisionDialog
          stepId={deciding.item.stepId}
          stepName={`${deciding.item.subjectLabel ?? deciding.item.subjectRecordTypeName} — ${deciding.item.stepName}`}
          decision={deciding.decision}
          invalidate={[
            ['approvals'],
            ['workflow', 'history', deciding.item.subjectRecordType, deciding.item.subjectRecordId],
          ]}
          onClose={() => {
            setDeciding(null);
          }}
        />
      )}
    </Section>
  );
}

/** The approval's subject, linked to its page when this session may view its type. */
export function SubjectLink({ item }: { item: ApprovalInboxItem }) {
  const session = useSession();
  const path = recordPath(item.subjectRecordType, item.subjectRecordId);
  const text = item.subjectLabel ?? `${item.subjectRecordTypeName} ${item.subjectRecordId}`;
  return path !== null && hasCapability(session.data, viewCapability(item.subjectRecordType)) ? (
    <Link component={RouterLink} to={path} sx={{ fontWeight: 600 }}>
      {text}
    </Link>
  ) : (
    <Typography variant="body2" sx={{ fontWeight: 600 }} component="span">
      {text}
    </Typography>
  );
}

function Delegations() {
  const session = useSession();
  const canDelegate = hasCapability(session.data, 'platform.approval_step:approve');
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [ending, setEnding] = useState<ApprovalDelegationDto | null>(null);
  const delegations = useQuery({
    queryKey: ['approvals', 'delegations'],
    queryFn: () => getData('/api/v1/approval-delegations', z.array(ApprovalDelegationSchema)),
  });
  const end = useMutation({
    mutationFn: (d: ApprovalDelegationDto) =>
      postJson(
        `/api/v1/approval-delegations/${d.id}/end`,
        {},
        ApprovalDelegationSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: d.version },
      ),
    onSuccess: async () => {
      setEnding(null);
      await queryClient.invalidateQueries({ queryKey: ['approvals', 'delegations'] });
    },
  });
  const subjectTypes = useQuery({
    queryKey: ['approvals', 'subject-types'],
    queryFn: () => getData('/api/v1/approvals/subject-types', z.array(SubjectTypeNameSchema)),
  });
  const typeName = (recordType: string) =>
    subjectTypes.data?.find((t) => t.recordType === recordType)?.displayName ?? recordType;
  const me = session.data?.userId;
  return (
    <Section
      title="Delegations"
      actions={
        canDelegate ? (
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              setCreating(true);
            }}
          >
            Delegate my approvals
          </Button>
        ) : undefined
      }
    >
      {delegations.isPending && <LoadingState label="Loading delegations" />}
      {delegations.isError && <ErrorState message={delegations.error.message} />}
      {delegations.data?.length === 0 && (
        <EmptyState
          title="No delegations"
          detail="While a delegation is current, the delegate can decide your approval steps under their own name."
        />
      )}
      {delegations.data !== undefined && delegations.data.length > 0 && (
        <Table size="small" aria-label="Delegations">
          <TableHead>
            <TableRow>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Covers</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {delegations.data.map((d) => (
              <TableRow key={d.id}>
                <TableCell>
                  <PersonName name={d.delegatorName} userId={d.delegatorUserId} />
                </TableCell>
                <TableCell>
                  <PersonName name={d.delegateName} userId={d.delegateUserId} />
                </TableCell>
                <TableCell>
                  {d.subjectRecordType === null ? 'All approvals' : typeName(d.subjectRecordType)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {d.validFrom} – {d.validTo}
                </TableCell>
                <TableCell>
                  <StatusChip
                    status={
                      d.status === 'active' && d.current
                        ? 'active'
                        : d.status === 'active'
                          ? 'scheduled'
                          : 'ended'
                    }
                  />
                </TableCell>
                <TableCell align="right">
                  {d.status === 'active' && d.delegatorUserId === me && canDelegate && (
                    <Button
                      size="small"
                      color="warning"
                      onClick={() => {
                        end.reset();
                        setEnding(d);
                      }}
                    >
                      End
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {creating && (
        <DelegateDialog
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
      <ConfirmDialog
        open={ending !== null}
        title="End delegation"
        message={`${ending?.delegateName ?? ''} will no longer be able to decide your approval steps.`}
        confirmLabel="End delegation"
        tone="warning"
        busy={end.isPending}
        error={end.isError ? errorMessage(end.error) : null}
        onCancel={() => {
          setEnding(null);
        }}
        onConfirm={() => {
          if (ending !== null) end.mutate(ending);
        }}
      />
    </Section>
  );
}

function DelegateDialog({ onClose }: { onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  // The business date (not the UTC date, which lags Dubai every evening) — as the API compares.
  const today = businessToday(session.data?.businessTimezone ?? 'Asia/Dubai');
  const [search, setSearch] = useState('');
  const [delegate, setDelegate] = useState<{ id: string; displayName: string } | null>(null);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [scope, setScope] = useState('');
  const subjectTypes = useQuery({
    queryKey: ['approvals', 'subject-types'],
    queryFn: () => getData('/api/v1/approvals/subject-types', z.array(SubjectTypeNameSchema)),
  });
  const candidates = useQuery({
    queryKey: ['approvals', 'candidates', search],
    enabled: search.trim().length >= 2,
    queryFn: () =>
      getData(
        `/api/v1/approval-delegations/candidates?q=${encodeURIComponent(search.trim())}`,
        z.array(DelegateCandidateSchema),
      ),
  });
  const save = useMutation({
    mutationFn: () =>
      postJson(
        '/api/v1/approval-delegations',
        {
          delegateUserId: delegate?.id ?? '',
          validFrom: from,
          validTo: to,
          ...(scope !== '' ? { subjectRecordType: scope } : {}),
        },
        ApprovalDelegationSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['approvals', 'delegations'] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title="Delegate my approvals"
      submitLabel="Delegate"
      canSubmit={delegate !== null && from !== '' && to !== '' && to >= from}
      submitting={save.isPending}
      error={save.isError ? errorMessage(save.error) : null}
      onClose={onClose}
      onSubmit={() => {
        save.mutate();
      }}
    >
      <Autocomplete
        options={candidates.data ?? []}
        getOptionLabel={(o) => o.displayName}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={delegate}
        onChange={(_e, value) => {
          setDelegate(value);
        }}
        onInputChange={(_e, value) => {
          setSearch(value);
        }}
        filterOptions={(o) => o}
        noOptionsText={
          search.trim().length < 2 ? 'Type at least two letters' : 'No active user matches'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Delegate"
            required
            helperText="The delegate must also hold the permission to approve the records covered."
          />
        )}
      />
      <TextField
        label="From"
        type="date"
        value={from}
        onChange={(e) => {
          setFrom(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <TextField
        label="To"
        type="date"
        value={to}
        onChange={(e) => {
          setTo(e.target.value);
        }}
        slotProps={{ inputLabel: { shrink: true } }}
        helperText="The last day the delegate may decide for you."
      />
      <TextField
        select
        label="Covers"
        value={scope}
        onChange={(e) => {
          setScope(e.target.value);
        }}
      >
        <MenuItem value="">All my approvals</MenuItem>
        {(subjectTypes.data ?? []).map((t) => (
          <MenuItem key={t.recordType} value={t.recordType}>
            {t.displayName}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}
